import { motion } from 'motion/react';
import { useSessionStore } from '@/shared/hooks/use-shallow-store';
import { DriverStatusKind } from '@/shared/lib/driver';
import { useT } from '@/shared/lib/i18n';

/**
 * Small banner that surfaces driver connection state for online play. Renders
 * `null` while the driver is `Idle` or `Ready`. Each online orchestrator
 * mounts one at the top of its page so the user always sees the reconnect
 * status, regardless of which phase they are in.
 *
 * Visual style matches the noir/caution-tape vocabulary: connecting → ink
 * pill with pulsing pop dot; error → und-tinted pill with bold warning.
 */
export const ConnectionBanner = () => {
  const t = useT();
  const driverStatus = useSessionStore((s) => s.driverStatus);

  if (
    driverStatus.kind === DriverStatusKind.Connecting ||
    driverStatus.kind === DriverStatusKind.Reconnecting
  ) {
    const attempt = driverStatus.kind === DriverStatusKind.Reconnecting ? driverStatus.attempt : 0;
    const label =
      driverStatus.kind === DriverStatusKind.Reconnecting
        ? `${t('online.banner.connecting')} (${attempt})`
        : t('online.banner.connecting');
    return (
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        className="eyebrow flex items-center gap-2 rounded-md border-2 border-ink bg-cream px-3 py-1.5 text-ink tracking-[0.16em] shadow-[2px_2px_0_var(--color-ink)]"
        role="status"
      >
        <motion.span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full bg-pop"
          animate={{ opacity: [0.4, 1, 0.4], scale: [0.85, 1.1, 0.85] }}
          transition={{ duration: 1.2, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
        />
        {label}
      </motion.div>
    );
  }
  if (
    driverStatus.kind === DriverStatusKind.Error ||
    driverStatus.kind === DriverStatusKind.Unauthorized ||
    driverStatus.kind === DriverStatusKind.RoomNotFound ||
    driverStatus.kind === DriverStatusKind.RoomFull ||
    driverStatus.kind === DriverStatusKind.KickedDisconnected
  ) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        className="eyebrow flex items-center gap-2 rounded-md border-2 border-und bg-cream px-3 py-1.5 text-und tracking-[0.16em] shadow-[2px_2px_0_var(--color-und)]"
        role="alert"
      >
        <span aria-hidden className="text-[12px]">
          ⚠
        </span>
        {t('online.banner.error')}
      </motion.div>
    );
  }
  return null;
};
